<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="800">
        <v-card>
            <v-card-title id="heading" class="d-flex justify-space-between">
                <div>
                    <h4>{{ company.company_name }}'s Users</h4>
                    <v-btn v-if="authUser.type === 'admin'" class="me-3" color="black" size="small"
                           @click="addUserDialogSwitch = true">
                        Add User
                    </v-btn>
                    <v-btn class="me-3" color="black" size="small" @click="inviteUserDialogSwitch = true">
                        Invite User
                    </v-btn>
                </div>
                <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
            </v-card-title>

            <v-container>
                <v-form v-if="formData.user" class="mb-3" validate-on="input lazy" @submit.prevent="updateRole">
                    <v-alert v-if="errors.length"
                             class="mb-3 bg-error"
                             closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col class="py-0" cols="12">
                            <h5>Change User Role</h5>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-select v-model="formData['user']" :items="users" :rules="[vRules.required]"
                                      density="compact" item-title="first_name" item-value="id" label="User">
                                <template v-slot:item="{ props, item }">
                                    <v-list-item :subtitle="item.raw.email"
                                                 :title="`${item.raw.first_name} ${item.raw.last_name}`"
                                                 v-bind="props"></v-list-item>
                                </template>
                            </v-select>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-select v-model="formData['role']" :items="filteredRoles" :rules="[vRules.required]"
                                      density="compact" item-title="title" item-value="id" label="Role" return-object/>
                        </v-col>

                        <v-col class="py-0 text-right" cols="12">
                            <v-btn color="primary" size="small" type="submit">Update</v-btn>
                        </v-col>
                    </v-row>

                </v-form>

                <v-tabs v-model="tab" bg-color="transparent" color="basil" grow>
                    <v-tab value="registered-users">
                        Registered Users
                    </v-tab>
                    <v-tab value="invited-users">
                        Invited Users
                    </v-tab>
                </v-tabs>
                <v-window v-model="tab">
                    <v-window-item value="registered-users">
                        <v-table>
                            <thead>
                            <tr class="font-weight-bold">
                                <td>First Name</td>
                                <td>Last Name</td>
                                <td>Email</td>
                                <td>Phone</td>
                                <td>Role</td>
                                <td>API Key</td>
                                <td>Actions</td>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="user in users" v-if="users.length" :key="user.id" class="text-left">
                                <td>{{ user.first_name }}</td>
                                <td>{{ user.last_name }}</td>
                                <td>{{ user.email }}</td>
                                <td>{{ user.contact_phone || user.mobile_phone }}</td>
                                <td>{{ user.role }}</td>
                                <td>{{ user.api_key }}</td>
                                <td>
                                    <v-btn-group
                                        v-if="authUser.type ==='admin' || (authUser.role?.includes('admin') && authUser.id !== user.user_id)"
                                        density="compact" divided
                                        variant="outlined">
                                        <v-btn class="text-warning" prepend-icon="mdi-account-switch"
                                               size="x-small" @click="changeRole(user)">Change Role
                                        </v-btn>
                                        <v-btn class="text-error" prepend-icon="mdi-account-multiple-minus"
                                               size="x-small" title="Remove User"
                                               @click="deleteUser(user)">Remove
                                        </v-btn>
                                        <v-menu>
                                            <template v-slot:activator="{ props: menuProps }">
                                                <v-btn icon="mdi-dots-vertical" v-bind="menuProps" variant="text" size="x-small"/>
                                            </template>
                                            <v-list density="compact">
                                                <v-list-item title="Reset Password" @click="openResetPassword(user)"/>
                                            </v-list>
                                        </v-menu>
                                    </v-btn-group>
                                </td>
                            </tr>
                            <tr v-else>
                                <td v-if="loading" class="text-center" colspan="7">
                                    <v-progress-circular color="primary" indeterminate size="small"/>
                                </td>
                                <td v-else class="text-center" colspan="7">No record found</td>
                            </tr>
                            </tbody>
                        </v-table>

                        <v-pagination v-if="totalPages > 1"
                                      class="my-4"
                                      v-model="currentPage"
                                      density="compact"
                                      :length="totalPages"
                                      justify="center"
                                      @update:model-value="fetchUsers"
                        ></v-pagination>
                    </v-window-item>
                    <v-window-item value="invited-users">
                        <v-table>
                            <thead>
                            <tr>
                                <th>First Name</th>
                                <th>Last Name</th>
                                <th>Email</th>
                                <th>Role</th>
                                <th>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="invitedUser in invitedUsers" v-if="invitedUsers.length" :key="invitedUser.id"
                                class="text-left">
                                <td>{{ invitedUser.first_name }}</td>
                                <td>{{ invitedUser.last_name }}</td>
                                <td>{{ invitedUser.email }}</td>
                                <td>{{ findRole(invitedUser.role_id) }}</td>
                                <td>
                                    <v-btn-group
                                        density="compact" divided
                                        variant="outlined">
                                        <v-btn :disabled="resendingInvite === invitedUser.id"
                                               :loading="resendingInvite === invitedUser.id"
                                               prepend-icon="mdi-email-send-outline"
                                               size="x-small" title="Resend Invitation"
                                               @click="resendInvitation(invitedUser)">Resend
                                        </v-btn>
                                        <v-btn class="text-error" prepend-icon="mdi-account-multiple-minus"
                                               size="x-small" title="Remove User"
                                               @click="deleteInvitation(invitedUser)">Remove
                                        </v-btn>
                                    </v-btn-group>
                                </td>
                            </tr>
                            <tr v-else>
                                <td v-if="loading" class="text-center" colspan="5">
                                    <v-progress-circular color="primary" indeterminate size="small"/>
                                </td>
                                <td v-else class="text-center" colspan="5">No record found</td>
                            </tr>
                            </tbody>
                        </v-table>

                        <v-pagination v-if="tab2TotalPages > 1"
                                      v-model="tab2CurrentPage"
                                      :length="tab2TotalPages"
                                      class="my-4"
                                      density="compact"
                                      justify="center"
                                      @update:model-value="fetchInvitedUsers"
                        ></v-pagination>
                    </v-window-item>

                </v-window>
            </v-container>

            <v-card-actions class="px-4 d-flex justify-end">
                <v-btn size="small" type="button"
                       @click="$emit('update:modelValue',false)">
                    Close
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <Add-User v-if="addUserDialogSwitch"
              v-model="addUserDialogSwitch"
              :company="company"
              :roles="roles"
              @update-users="updateUsers"
    />

    <Invite-User v-if="inviteUserDialogSwitch"
                 v-model="inviteUserDialogSwitch"
                 :company="company"
                 :roles="roles"
                 @update-invited-user="(newUser) => updateUsers(newUser,'invite')"
                 @refresh-invites="fetchInvitedUsers"
    />

    <!-- Reset Password Dialog -->
    <v-dialog v-model="resetPasswordDialog" max-width="450" persistent>
        <v-card>
            <v-card-title>Reset Password</v-card-title>
            <v-card-subtitle v-if="resetPasswordUser" class="pb-0">
                {{ resetPasswordUser.first_name }} {{ resetPasswordUser.last_name }} ({{ resetPasswordUser.email }})
            </v-card-subtitle>
            <v-card-text>
                <v-radio-group v-model="resetPasswordMode" class="mb-2">
                    <v-radio label="Generate random password" value="random"/>
                    <v-radio label="Set specific password" value="manual"/>
                </v-radio-group>
                <v-text-field v-if="resetPasswordMode === 'manual'" v-model="manualPassword"
                              :rules="[v => !!v || 'Required', v => (v && v.length >= 8) || 'Min 8 characters']"
                              autocomplete="new-password" label="New Password" type="password" density="compact"/>
                <v-alert type="info" variant="tonal" density="compact" class="mt-2">
                    The new password will be emailed to the user. They will be required to change it on first login.
                </v-alert>
            </v-card-text>
            <v-card-actions class="d-flex justify-end px-4 pb-4">
                <v-btn @click="resetPasswordDialog = false" variant="text">Cancel</v-btn>
                <v-btn color="primary" :loading="resetPasswordLoading"
                       :disabled="resetPasswordMode === 'manual' && (!manualPassword || manualPassword.length < 8)"
                       @click="submitResetPassword">Reset Password
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {ref, reactive, onMounted, computed} from "vue";
import {useSnackbarStore} from "@/stores/snackBarStore";
import AddUser from "@/components/dialogs/companies/Users/Create.vue";
import InviteUser from "@/components/dialogs/companies/Users/Invite.vue";

const {showSnackbar} = useSnackbarStore();
const authUser = useAuthStore().user;
const props = defineProps({
    modelValue: Boolean,
    company: Object,
    allUsers: Array,
    roles: Array,
});

const resetPasswordDialog = ref(false);
const resetPasswordUser = ref(null);
const resetPasswordMode = ref('random');
const resetPasswordLoading = ref(false);
const manualPassword = ref(null);
const formData = reactive({
    user: null,
    role: null,
    company: props.company.id,
});
const emits = defineEmits(['update:modelValue']);
const inviteUserDialogSwitch = ref(false);
const addUserDialogSwitch = ref(false);
const dialogSwitch = ref(props.modelValue);
const tab = ref('registered-users');
const totalPages = ref(1);
const currentPage = ref(1);
const tab2TotalPages = ref(1);
const tab2CurrentPage = ref(1);
const selectedUser = ref(null);
const invitedUsers = ref([]);
const loading = ref(true);
const errors = ref([]);
const users = ref([]);

onMounted(() => {
    fetchUsers();
    fetchInvitedUsers();
});

const filteredRoles = computed(() => {
    if (selectedUser.value) {
        return props.roles.filter((role) => ((props.company.portal_id === role.portal_id && role.role_type === selectedUser.value.role_type) || role.role_type === 'api'));
    } else {
        return props.roles.filter((role) => (props.company.portal_id === role.portal_id || role.role_type === 'api'));
    }
});

// Look the title up in the FULL role list, not filteredRoles. filteredRoles is
// the list offered by the "Change Role" select, so once a user is selected it
// narrows to that user's own role_type - while this is used to label the
// Invited Users table, whose invites can carry any role_type. The old lookup
// returned undefined for those and then read ['title'] off it, so clicking
// Change Role on an API-type user and opening Invited Users threw a TypeError
// and blanked the dialog. Fall back to the raw id rather than to nothing, so a
// role that has since been deleted still renders something.
const findRole = (roleId) => props.roles?.find((role) => role.id === roleId)?.title ?? roleId;
const fetchUsers = () => {
    send('get', `users?company=${props.company.id}&page=${currentPage.value}`)
        .then((response) => {
            users.value = response.data.data.data;
            totalPages.value = response.data.data.last_page;
            currentPage.value = response.data.data.current_page;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Users could not be loaded', 'error')
        }).finally(() => loading.value = false);
}
const fetchInvitedUsers = () => {
    send('get', `invited-users`, {company: props.company.id, page: tab2CurrentPage.value})
        .then((response) => {
            invitedUsers.value = response.data.data.users.data;
            tab2TotalPages.value = response.data.data.users.last_page;
            tab2CurrentPage.value = response.data.data.users.current_page;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Users could not be loaded', 'error')
        })
}
const updateUsers = (newUser, type = 'user') => {
    if (type === 'user') {
        users.value.unshift(newUser);
    } else {
        if (invitedUsers.value.findIndex((user) => user.id === newUser.id) === -1) {
            invitedUsers.value.unshift(newUser)
        }
    }
}
const changeRole = (user) => {
    selectedUser.value = user;
    formData['user'] = selectedUser.value.id;
    formData['role'] = props.roles.find((role) => role.id === selectedUser.value.role_id);
    document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
}
const updateRole = () => {
    errors.value = [];
    send('put', `users/${formData.user}/role`, {company: props.company.id, role: formData.role.id})
        .then((response) => {
            showSnackbar(response.data.message);
            if (response.data.success) {
                users.value.map((user) => {
                    if (user.user_id === formData.user) {
                        user.role_id = formData.role.id;
                        user.role = formData.role.title;
                    }
                });
                formData.user = null;
                formData.role = null;
            }
        })
        .catch(function (errorResponse) {
            if (errorResponse.response.data.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
}
const deleteUser = (user) => {
    formData['user'] = null;
    formData['role'] = null;
    if (confirm(`Are you sure to remove "${user.first_name} ${user.last_name}" from ${props.company.company_name}?`)) {
        send('put', `users/${user.id}/company`, {company: props.company.id})
            .then((response) => {
                if (response.data.success) {
                    users.value.map((item, index) => {
                        if (item.id === user.id) {
                            users.value.splice(index, 1);
                        }
                    });
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                console.log(error)
                showSnackbar(`"${user.first_name} ${user.last_name}" user could not be deleted`, 'error')
            })
    }
}
const openResetPassword = (user) => {
    resetPasswordUser.value = user;
    resetPasswordMode.value = 'random';
    manualPassword.value = null;
    resetPasswordDialog.value = true;
}
const submitResetPassword = () => {
    resetPasswordLoading.value = true;
    const userId = resetPasswordUser.value.user_id ?? resetPasswordUser.value.id;
    const payload = resetPasswordMode.value === 'manual' ? {password: manualPassword.value} : {};
    send('put', `users/${userId}/admin-reset-password`, payload)
        .then((response) => {
            if (response.data.success) {
                showSnackbar(response.data.message);
                resetPasswordDialog.value = false;
            }
        })
        .catch((error) => {
            showSnackbar(error.response?.data?.message ?? 'Failed to reset password', 'error');
        })
        .finally(() => resetPasswordLoading.value = false);
}
const resendingInvite = ref(null);
const resendInvitation = (invitation) => {
    resendingInvite.value = invitation.id;
    send('post', `invited-users/${invitation.id}/resend`)
        .then((response) => showSnackbar(response.data.message))
        .catch((error) => {
            // A resend within 30 seconds of the last email comes back as 429 and asks them to wait.
            const fallback = `The invitation to ${invitation.email} could not be sent`;
            showSnackbar(error.response?.data?.message ?? fallback, error.response?.status === 429 ? 'warning' : 'error');
        })
        .finally(() => resendingInvite.value = null);
};
const deleteInvitation = (invitation) => {
    if (confirm(`Are you sure to remove "${invitation.first_name} ${invitation.last_name}" from invitation list?`)) {
        send('delete', `invited-users/${invitation.id}`, null)
            .then((response) => {
                if (response.data.success) {
                    invitedUsers.value.map((item, index) => {
                        if (item.id === invitation.id) {
                            invitedUsers.value.splice(index, 1);
                        }
                    });
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                console.log(error)
                showSnackbar(`"${invitation.first_name} ${invitation.last_name}" user's invitation could not be deleted`, 'error')
            })
    }
}

</script>
